import type {DiscountType, Price} from '@/redux/v1/models';

import {LabelSmallBold, LabelSmall} from '@travel-frontend/ui-components';
import {useTranslation} from 'react-i18next';

import {HelpTooltip} from '@/components/HelpTooltip';
import {DiscountBadge} from '@/components/PriceTag/components/DiscountedTotalPriceOriginal/components/DiscountBadge';
import {TotalPriceOriginal} from '@/components/PriceTag/components/DiscountedTotalPriceOriginal/components/TotalPriceOriginal';
import {TotalPriceOriginalContainer} from '@/components/PriceTag/components/DiscountedTotalPriceOriginal/components/TotalPriceOriginalContainer';
import {config} from '@/config';
import {percentFormat, priceFormat} from '@/utils/numberFormat';

const DISCOUNT_PERCENTAGE_THRESHOLD = 5;

type Props = {
  totalPrice: Price;
  totalPriceOriginal?: Price | null;
  totalDiscount: Price | null;
  totalDiscountOriginal?: Price;
  totalDiscountHint?: string | null;
  fractionDigits?: number;
  discountType?: DiscountType;
  discountPercentage: number;
  canShowPriceCache: boolean;
  size: 'small' | 'medium' | 'large';
};

export const DiscountedTotalPriceOriginal = ({
  totalPrice,
  totalPriceOriginal,
  totalDiscount,
  totalDiscountOriginal,
  totalDiscountHint,
  fractionDigits,
  discountType,
  discountPercentage: discountPercentageOption,
  canShowPriceCache,
  size,
}: Props) => {
  const {t} = useTranslation();

  if (
    totalPriceOriginal == null ||
    totalDiscount === null ||
    totalPriceOriginal.amount <= totalPrice.amount ||
    (totalDiscount.amount <= 0 && discountType !== 'ISEARCH_DISCOUNT')
  ) {
    return null;
  }

  if (!canShowPriceCache && discountType === 'PRICE_CACHE') {
    return null;
  }

  const isDiscountInCHF = totalDiscount.currency === 'CHF';
  const isDiscountOriginalInEUR = totalDiscountOriginal?.currency === 'EUR';

  let discountPercentage: number | null = null;
  if (discountType === 'PRICE_CACHE' || isDiscountInCHF) {
    discountPercentage = Math.round(
      (totalDiscount.amount / totalPriceOriginal.amount) * 100
    );
  } else if (discountType === 'ISEARCH_DISCOUNT') {
    discountPercentage = discountPercentageOption;
  }

  if (
    (discountType === 'PRICE_CACHE' || discountType === 'ISEARCH_DISCOUNT') &&
    discountPercentage !== null &&
    discountPercentage < DISCOUNT_PERCENTAGE_THRESHOLD
  ) {
    return null;
  }

  const badgeContent =
    discountPercentage === null
      ? priceFormat(
          {amount: totalDiscount.amount * -1, currency: totalDiscount.currency},
          {
            fractionDigits: 0,
          }
        )
      : percentFormat(discountPercentage * -1, {maximumFractionDigits: 0});

  const euroDiscountExchange = totalDiscountOriginal
    ? priceFormat(
        {
          amount: totalDiscountOriginal.amount,
          // fixed "EUR" for tooltip
          currency: 'EUR',
        },
        {
          fractionDigits: 2,
          locale: 'de-DE',
        }
      )
    : null;

  const exchangeText =
    isDiscountInCHF && isDiscountOriginalInEUR ? euroDiscountExchange : '';

  const tooltipContent = discountType
    ? {
        NO_DISCOUNT: '',
        SPECIAL_DISCOUNT: totalDiscountHint,
        ISEARCH_DISCOUNT: '',
        PRICE_CACHE: t('discountNotice.content'),
      }[discountType]
    : '';

  return (
    <TotalPriceOriginalContainer>
      <DiscountBadge size={size}>{badgeContent}</DiscountBadge>
      <TotalPriceOriginal size={size}>
        {priceFormat(totalPriceOriginal, {
          fractionDigits,
        })}
      </TotalPriceOriginal>
      {config.priceTag.showDiscountTooltip && (
        <HelpTooltip
          placement="bottom"
          content={
            <>
              <LabelSmallBold>
                {t('discountNotice.title', {
                  context: exchangeText ? 'withExchange' : '',
                  price: totalDiscount?.amount,
                  currency: totalDiscount?.currency,
                  // only for .ch
                  exchange: exchangeText,
                })}
              </LabelSmallBold>
              {tooltipContent && (
                <LabelSmall whiteSpace="pre-line" marginTop="8px">
                  {tooltipContent}
                </LabelSmall>
              )}
            </>
          }
        />
      )}
    </TotalPriceOriginalContainer>
  );
};
